export const dailyIslamicContentCategoryIds = ["ayah", "hadith", "dua"] as const;

export type DailyIslamicContentCategoryId = (typeof dailyIslamicContentCategoryIds)[number];

export type DailyAyahContent = {
  arabic: string;
  ayahNumber: number;
  reference: string;
  surahName: string;
  surahNumber: number;
  translation: string;
  translationSource: string;
  verseKey: string;
};

export type DailyHadithContent = {
  arabic: string | null;
  collection: string;
  grade: string | null;
  hadithId: string;
  narrator: string | null;
  number: string;
  reference: string;
  translation: string;
};

export type DailyDuaContent = {
  arabic: string;
  category: string | null;
  duaId: string;
  reference: string | null;
  source: string | null;
  title: string;
  translation: string;
  transliteration: string | null;
};

export type DailyIslamicContentProjectionRecord = {
  ayah: DailyAyahContent | null;
  date: string;
  dua: DailyDuaContent | null;
  expiresAt: unknown;
  generatedAt: unknown;
  hadith: DailyHadithContent | null;
  organizationId: string;
  schemaVersion: 1;
  source: "p3_daily_islamic_content";
  sourceMode: "automatic" | "curated";
  timeZone: string;
};

export const approvedDailyAyahOptions: DailyAyahContent[] = [
  {
    arabic: "بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ",
    ayahNumber: 1,
    reference: "quran:1:1",
    surahName: "Al-Fatihah",
    surahNumber: 1,
    translation: "In the name of Allah, the Entirely Merciful, the Especially Merciful.",
    translationSource: "sahih_international",
    verseKey: "1:1",
  },
  {
    arabic:
      "رَبَّنَآ ءَاتِنَا فِي ٱلدُّنْيَا حَسَنَةً وَفِي ٱلْـَٔاخِرَةِ حَسَنَةً وَقِنَا عَذَابَ ٱلنَّارِ",
    ayahNumber: 201,
    reference: "quran:2:201",
    surahName: "Al-Baqarah",
    surahNumber: 2,
    translation:
      "Our Lord, give us good in this world and good in the Hereafter, and protect us from the punishment of the Fire.",
    translationSource: "sahih_international",
    verseKey: "2:201",
  },
  {
    arabic:
      "رَبَّنَا لَا تُزِغْ قُلُوبَنَا بَعْدَ إِذْ هَدَيْتَنَا وَهَبْ لَنَا مِن لَّدُنكَ رَحْمَةً ۚ إِنَّكَ أَنتَ ٱلْوَهَّابُ",
    ayahNumber: 8,
    reference: "quran:3:8",
    surahName: "Ali 'Imran",
    surahNumber: 3,
    translation:
      "Our Lord, let not our hearts deviate after You have guided us and grant us from Yourself mercy. Indeed, You are the Bestower.",
    translationSource: "sahih_international",
    verseKey: "3:8",
  },
  {
    arabic:
      "إِذْ أَوَى ٱلْفِتْيَةُ إِلَى ٱلْكَهْفِ فَقَالُوا۟ رَبَّنَآ ءَاتِنَا مِن لَّدُنكَ رَحْمَةًۭ وَهَيِّئْ لَنَا مِنْ أَمْرِنَا رَشَدًۭا",
    ayahNumber: 10,
    reference: "quran:18:10",
    surahName: "Al-Kahf",
    surahNumber: 18,
    translation:
      "Our Lord, grant us from Yourself mercy and prepare for us from our affair right guidance.",
    translationSource: "sahih_international",
    verseKey: "18:10",
  },
  {
    arabic: "رَّبِّ زِدْنِي عِلْمًا",
    ayahNumber: 114,
    reference: "quran:20:114",
    surahName: "Taha",
    surahNumber: 20,
    translation: "My Lord, increase me in knowledge.",
    translationSource: "sahih_international",
    verseKey: "20:114",
  },
  {
    arabic:
      "وَٱلَّذِينَ يَقُولُونَ رَبَّنَا هَبْ لَنَا مِنْ أَزْوَٰجِنَا وَذُرِّيَّـٰتِنَا قُرَّةَ أَعْيُنٍۢ وَٱجْعَلْنَا لِلْمُتَّقِينَ إِمَامًا",
    ayahNumber: 74,
    reference: "quran:25:74",
    surahName: "Al-Furqan",
    surahNumber: 25,
    translation:
      "Our Lord, grant us from among our spouses and offspring comfort to our eyes and make us an example for the righteous.",
    translationSource: "sahih_international",
    verseKey: "25:74",
  },
  {
    arabic:
      "وَٱلَّذِينَ جَآءُو مِنۢ بَعْدِهِمْ يَقُولُونَ رَبَّنَا ٱغْفِرْ لَنَا وَلِإِخْوَٰنِنَا ٱلَّذِينَ سَبَقُونَا بِٱلْإِيمَـٰنِ وَلَا تَجْعَلْ فِى قُلُوبِنَا غِلًّۭا لِّلَّذِينَ ءَامَنُوا۟ رَبَّنَآ إِنَّكَ رَءُوفٌۭ رَّحِيمٌ",
    ayahNumber: 10,
    reference: "quran:59:10",
    surahName: "Al-Hashr",
    surahNumber: 59,
    translation:
      "Our Lord, forgive us and our brothers who preceded us in faith, and do not place resentment in our hearts toward those who believe. Our Lord, You are Kind and Merciful.",
    translationSource: "sahih_international",
    verseKey: "59:10",
  },
];

export const approvedDailyHadithOptions: DailyHadithContent[] = [
  {
    arabic: "إِنَّمَا الأَعْمَالُ بِالنِّيَّاتِ",
    collection: "bukhari",
    grade: null,
    hadithId: "hadith:bukhari:1",
    narrator: "Umar ibn al-Khattab",
    number: "1",
    reference: "Sahih al-Bukhari 1",
    translation: "Actions are only by intentions.",
  },
  {
    arabic: null,
    collection: "nawawi40",
    grade: null,
    hadithId: "hadith:nawawi40:6",
    narrator: "An-Nu'man ibn Bashir",
    number: "6",
    reference: "Nawawi 40 Hadith 6",
    translation: "The lawful is clear and the unlawful is clear.",
  },
  {
    arabic: "لاَ يُؤْمِنُ أَحَدُكُمْ حَتَّى يُحِبَّ لأَخِيهِ مَا يُحِبُّ لِنَفْسِهِ",
    collection: "bukhari",
    grade: null,
    hadithId: "hadith:bukhari:13",
    narrator: "Anas ibn Malik",
    number: "13",
    reference: "Sahih al-Bukhari 13",
    translation:
      "None of you truly believes until he loves for his brother what he loves for himself.",
  },
  {
    arabic:
      "اتَّقِ اللَّهَ حَيْثُمَا كُنْتَ، وَأَتْبِعِ السَّيِّئَةَ الْحَسَنَةَ تَمْحُهَا، وَخَالِقِ النَّاسَ بِخُلُقٍ حَسَنٍ",
    collection: "nawawi40",
    grade: "hasan",
    hadithId: "hadith:nawawi40:18",
    narrator: "Abu Dharr and Mu'adh ibn Jabal",
    number: "18",
    reference: "Nawawi 40 Hadith 18",
    translation:
      "Have taqwa of Allah wherever you are, follow a bad deed with a good deed, and treat people with good character.",
  },
  {
    arabic:
      "مَنْ سَلَكَ طَرِيقًا يَلْتَمِسُ فِيهِ عِلْمًا سَهَّلَ اللَّهُ لَهُ بِهِ طَرِيقًا إِلَى الْجَنَّةِ",
    collection: "muslim",
    grade: null,
    hadithId: "hadith:muslim:2699a",
    narrator: "Abu Hurairah",
    number: "2699a",
    reference: "Sahih Muslim 2699a",
    translation:
      "Whoever follows a path seeking knowledge, Allah will make a path to Paradise easy for him.",
  },
];

export const approvedDailyDuaOptions: DailyDuaContent[] = [
  {
    arabic:
      "رَبَّنَا آتِنَا فِي الدُّنْيَا حَسَنَةً وَفِي الآخِرَةِ حَسَنَةً وَقِنَا عَذَابَ النَّارِ",
    category: "quran",
    duaId: "dua:quran:2:201",
    reference: "Quran 2:201",
    source: "Quran",
    title: "Good in this world and the Hereafter",
    translation:
      "Our Lord, grant us good in this world and good in the Hereafter, and protect us from the punishment of the Fire.",
    transliteration:
      "Rabbana atina fid-dunya hasanatan wa fil-akhirati hasanatan wa qina adhaban-nar",
  },
  {
    arabic: "رَبِّ زِدْنِي عِلْمًا",
    category: "quran",
    duaId: "dua:quran:20:114",
    reference: "Quran 20:114",
    source: "Quran",
    title: "Increase me in knowledge",
    translation: "My Lord, increase me in knowledge.",
    transliteration: "Rabbi zidni ilma",
  },
  {
    arabic:
      "رَبَّنَا لَا تُزِغْ قُلُوبَنَا بَعْدَ إِذْ هَدَيْتَنَا وَهَبْ لَنَا مِن لَّدُنكَ رَحْمَةً ۚ إِنَّكَ أَنتَ ٱلْوَهَّابُ",
    category: "quran",
    duaId: "dua:quran:3:8",
    reference: "Quran 3:8",
    source: "Quran",
    title: "Steadfast hearts",
    translation:
      "Our Lord, let not our hearts deviate after You have guided us and grant us from Yourself mercy. Indeed, You are the Bestower.",
    transliteration:
      "Rabbana la tuzigh qulubana ba'da idh hadaytana wa hab lana min ladunka rahmah innaka antal-wahhab",
  },
  {
    arabic: "رَبَّنَآ ءَاتِنَا مِن لَّدُنكَ رَحْمَةًۭ وَهَيِّئْ لَنَا مِنْ أَمْرِنَا رَشَدًۭا",
    category: "quran",
    duaId: "dua:quran:18:10",
    reference: "Quran 18:10",
    source: "Quran",
    title: "Mercy and right guidance",
    translation:
      "Our Lord, grant us from Yourself mercy and prepare for us from our affair right guidance.",
    transliteration: "Rabbana atina min ladunka rahmatan wa hayyi lana min amrina rashada",
  },
  {
    arabic:
      "رَبَّنَا هَبْ لَنَا مِنْ أَزْوَٰجِنَا وَذُرِّيَّـٰتِنَا قُرَّةَ أَعْيُنٍۢ وَٱجْعَلْنَا لِلْمُتَّقِينَ إِمَامًا",
    category: "quran",
    duaId: "dua:quran:25:74",
    reference: "Quran 25:74",
    source: "Quran",
    title: "Family and righteous example",
    translation:
      "Our Lord, grant us from among our spouses and offspring comfort to our eyes and make us an example for the righteous.",
    transliteration:
      "Rabbana hab lana min azwajina wa dhurriyyatina qurrata a'yunin waj'alna lil-muttaqina imama",
  },
  {
    arabic:
      "رَبَّنَا ٱغْفِرْ لَنَا وَلِإِخْوَٰنِنَا ٱلَّذِينَ سَبَقُونَا بِٱلْإِيمَـٰنِ وَلَا تَجْعَلْ فِى قُلُوبِنَا غِلًّۭا لِّلَّذِينَ ءَامَنُوا۟ رَبَّنَآ إِنَّكَ رَءُوفٌۭ رَّحِيمٌ",
    category: "quran",
    duaId: "dua:quran:59:10",
    reference: "Quran 59:10",
    source: "Quran",
    title: "Forgiveness for believers",
    translation:
      "Our Lord, forgive us and our brothers who preceded us in faith, and do not place resentment in our hearts toward those who believe. Our Lord, You are Kind and Merciful.",
    transliteration:
      "Rabbana ighfir lana wa li-ikhwanina alladhina sabaquna bil-iman wa la taj'al fi qulubina ghillan lilladhina amanu rabbana innaka ra'ufur rahim",
  },
];

export function buildDailyIslamicContentProjection(input: {
  ayahReference?: string | null;
  date: string;
  duaReference?: string | null;
  expiresAt: unknown;
  generatedAt: unknown;
  hadithReference?: string | null;
  organizationId: string;
  sourceMode: "automatic" | "curated";
  timeZone: string;
}): DailyIslamicContentProjectionRecord {
  const automaticOffset = stableDailyIndex(input.date, input.organizationId);

  return {
    ayah: contentForReference(approvedDailyAyahOptions, input.ayahReference, automaticOffset),
    date: input.date,
    dua: contentForReference(approvedDailyDuaOptions, input.duaReference, automaticOffset + 2),
    expiresAt: input.expiresAt,
    generatedAt: input.generatedAt,
    hadith: contentForReference(
      approvedDailyHadithOptions,
      input.hadithReference,
      automaticOffset + 1,
    ),
    organizationId: input.organizationId,
    schemaVersion: 1,
    source: "p3_daily_islamic_content",
    sourceMode: input.sourceMode,
    timeZone: input.timeZone,
  };
}

type DailyReferenceOption = {
  reference?: string | null;
  hadithId?: string | null;
  duaId?: string | null;
};

function contentForReference<T extends DailyReferenceOption>(
  options: readonly T[],
  reference: string | null | undefined,
  fallbackOffset: number,
): T | null {
  if (reference === null) {
    return null;
  }
  if (reference) {
    return (
      options.find((option) =>
        [option.reference, option.hadithId, option.duaId].includes(reference),
      ) ?? null
    );
  }
  return options[fallbackOffset % options.length] ?? null;
}

function stableDailyIndex(date: string, organizationId: string): number {
  const value = `${organizationId}:${date}`;
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
}
